import { getUniqueId } from "@jugl-web/utils";
import {
  PaginatedRequestParams,
  PaginatedResponse,
  QueryParamsWithEntityId,
  RtkEmptySplitApi,
} from "../../types";
import {
  WebChatBotConversationDto,
  WhatsappAgentModel,
  WhatsappMessageModel,
  Workflow,
  WorkflowLog,
  WorkflowParams,
} from "./models";
import { InstagramAccountModel } from "./models/InstagramAccount";
import {
  InstagramConversationModel,
  transformInstagramConversationDtoToModel,
} from "./models/InstagramConversation";
import { InstagramConversationDto } from "./models/InstagramConversation/InstagramConversationDto";
import { InstagramMessageModel } from "./models/InstagramMessage/InstagramMessageModel";
import { TwilioAccountModel } from "./models/TwilioAccount";
import { TwilioConversationModel } from "./models/TwilioConversation";
import {
  TwilioMessageModel,
  transformTwilioMessageDtoToModel,
} from "./models/TwilioMessage";
import { WebChatBotAccountModel } from "./models/WebChatBotAccount/WebChatBotAccountModel";
import { WebChatBotConversationModel } from "./models/WebChatBotConversation/WebChatBotConversationModel";
import { transformWebChatBotConversationDtoToModel } from "./models/WebChatBotConversation/utils";
import { WebChatBotMessageDto } from "./models/WebChatBotMessage/WebChatBotMessageDto";
import { WebChatBotMessageModel } from "./models/WebChatBotMessage/WebChatBotMessageModel";
import { transformWebChatBotMessageDtoToModel } from "./models/WebChatBotMessage/utils";
import { WhatsappAgentLogModel } from "./models/WhatsappAgentLog";
import {
  WhatsappConversationModel,
  transformWhatsappConversationDtoToModel,
} from "./models/WhatsappConversation";
import { WhatsappConversationDto } from "./models/WhatsappConversation/WhatsappConversationDto";
import {
  WhatsappTemplateDto,
  WhatsappTemplateModel,
  transformWhatsappTemplateDtoToModel,
} from "./models/WhatsappTemplate";
import { IntegrationsApiTag } from "./tags";
import {
  AgentConfigData,
  AgentConfigDto,
  AIAgentInsights,
  AICustomerInsights,
  CalendlyEventType,
  ConnectionDto,
  ConnectionType,
  CRMChannel,
  CRMConversationFilter,
  CRMConversationMode,
  CRMUnreadCounts,
  MarkConversationAsReadParams,
  SendInstagramMessageMutationParams,
  SendTwilioMessageMutationParams,
  SendWhatsappMessageMutationParams,
  SquareupLocation,
  WhatsappBusinessVerificationStatus,
  WhatsappProfile,
  WhatsappQualityRating,
  WhatsappTemplateCategory,
  WhatsappTemplatePayload,
  WhatsappTemplateStatus,
} from "./types";
import { AIAgentAnalyticsItemDto } from "./models/AIAgentAnalyticsItem/AIAgentAnalyticsItemDto";
import { AIAgentAnalyticsItemModel } from "./models/AIAgentAnalyticsItem/AIAgentAnalyticsItemModel";
import { transformAIAgentAnalyticsItemsDtoToModels } from "./models/AIAgentAnalyticsItem/utils";

export const addIntegrationsApi = (emptySplitApi: RtkEmptySplitApi) => {
  const apiWithTags = emptySplitApi.enhanceEndpoints({
    addTagTypes: [
      IntegrationsApiTag.whatsappMessageMedia,
      IntegrationsApiTag.whatsappTemplate,
      IntegrationsApiTag.crmConfig,
      IntegrationsApiTag.whatsappProfile,
      IntegrationsApiTag.workflowLogs,
      IntegrationsApiTag.instagramMessage,
      IntegrationsApiTag.shopifyAccount,
      IntegrationsApiTag.calendlyConnection,
      IntegrationsApiTag.squareupConnection,
      IntegrationsApiTag.agentConfig,
    ],
  });

  const integrationsApi = apiWithTags.injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      // WhatsApp

      getWhatsappTemplates: builder.query<
        {
          data: WhatsappTemplateModel[];
          next_page: string | null;
        },
        {
          entityId: string;
          params?: {
            type?: WhatsappTemplateCategory;
            status?: WhatsappTemplateStatus;
            enabled?: boolean;
            page?: string;
          };
        }
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/template`,
          params,
        }),
        transformResponse: (response: {
          data: WhatsappTemplateDto[];
          next_page: string | null;
        }) => ({
          ...response,
          data: response.data.map(transformWhatsappTemplateDtoToModel),
        }),
      }),
      createWhatsappTemplate: builder.mutation<
        WhatsappTemplateModel,
        { entityId: string; data: WhatsappTemplatePayload }
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/template`,
          method: "POST",
          // TODO: temp solution until backend picks up
          data: {
            ...data,
            category:
              data.type === WhatsappTemplateCategory.message
                ? "MARKETING"
                : "UTILITY",
            action: data.action || getUniqueId(),
          },
          silentError: true,
        }),
        transformResponse: (response: WhatsappTemplateDto) =>
          transformWhatsappTemplateDtoToModel(response),
      }),
      deleteWhatsappTemplate: builder.mutation<
        void,
        { entityId: string; templateId: string }
      >({
        query: ({ entityId, templateId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/template/${templateId}`,
          method: "DELETE",
        }),
      }),
      getWhatsappTemplate: builder.query<
        WhatsappTemplateModel,
        { entityId: string; templateId: string }
      >({
        query: ({ entityId, templateId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/template/${templateId}`,
        }),
        transformResponse: (response: WhatsappTemplateDto) =>
          transformWhatsappTemplateDtoToModel(response),
        providesTags: (_result, _error, { templateId }) => [
          { type: IntegrationsApiTag.whatsappTemplate, id: templateId },
        ],
      }),
      updateWhatsappTemplate: builder.mutation<
        WhatsappTemplateModel,
        {
          entityId: string;
          data: WhatsappTemplatePayload;
          templateId: string;
        }
      >({
        query: ({ entityId, data, templateId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/template/${templateId}`,
          method: "PUT",
          data,
          silentError: true,
        }),
        transformResponse: (response: WhatsappTemplateDto) =>
          transformWhatsappTemplateDtoToModel(response),
        invalidatesTags: (result, error, { templateId }) =>
          result
            ? [{ type: IntegrationsApiTag.whatsappTemplate, id: templateId }]
            : [],
      }),
      connectWhatsappAgentAccount: builder.mutation<
        WhatsappAgentModel,
        {
          entityId: string;
          data: {
            code: string;
            waba_id: string;
            phone_number_id: string;
          };
        }
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/account`,
          method: "POST",
          data: { ...data, redirect_url: "" },
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),
      disconnectWhatsappAgentAccount: builder.mutation<
        void,
        { entityId: string }
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/account`,
          method: "DELETE",
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),
      changeWhatsappTemplateStatus: builder.mutation<
        void,
        { entityId: string; templateId: string; isEnabled: boolean }
      >({
        query: ({ entityId, templateId, isEnabled }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/template/${templateId}`,
          method: "PUT",
          data: {
            enabled: isEnabled,
          },
        }),
      }),
      getAgentLogs: builder.query<
        PaginatedResponse<WhatsappAgentLogModel>,
        PaginatedRequestParams<{ entityId: string }>
      >({
        query: ({ entityId, ...params }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/logs`,
          params: {
            ...params,
          },
        }),
      }),
      registerWhatsappAgentAccount: builder.mutation<
        { success: boolean },
        { entityId: string }
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/account/register`,
          method: "POST",
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),
      getWhatsappConversations: builder.query<
        PaginatedResponse<WhatsappConversationModel>,
        PaginatedRequestParams<{
          entityId: string;
          filter:
            | CRMConversationFilter
            | CRMConversationMode.onAIAutoReply
            | CRMConversationMode.waitingResponse
            | string;
          search_term?: string;
        }>
      >({
        query: ({ entityId, filter, page, page_size, search_term }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/conversation`,
          params: {
            filter,
            page,
            page_size,
            search_term: search_term?.trim() || undefined,
          },
        }),
        transformResponse: (
          response: PaginatedResponse<WhatsappConversationDto>
        ) => ({
          ...response,
          data: response.data.map(transformWhatsappConversationDtoToModel),
        }),
      }),
      getBlockedContacts: builder.query<string[], { entityId: string }>({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/blocked`,
        }),
        transformResponse: (response: {
          data: {
            messaging_product: "whatsapp";
            wa_id: string;
          }[];
        }) => response.data.map((item) => item.wa_id),
      }),
      manageContact: builder.mutation<
        void,
        { entityId: string; userId: string; type: "block" | "unblock" }
      >({
        query: ({ entityId, userId, type }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/blocked`,
          method: type === "block" ? "POST" : "DELETE",
          data: {
            users: [{ user: userId }],
          },
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const updateBlockedContacts = dispatch(
            integrationsApi.util.updateQueryData(
              "getBlockedContacts",
              {
                entityId: args.entityId,
              },
              (blockedContactsDraft) => {
                if (args.type === "block") {
                  return [...blockedContactsDraft, args.userId];
                }

                return blockedContactsDraft.filter(
                  (blockedContact) => blockedContact !== args.userId
                );
              }
            )
          );
          queryFulfilled.catch(() => {
            updateBlockedContacts.undo();
          });
        },
      }),
      getWhatsappMessages: builder.query<
        PaginatedResponse<WhatsappMessageModel>,
        {
          entityId: string;
          params: PaginatedRequestParams<{
            conv_id: string;
            from: string;
          }>;
        }
      >({
        queryFn: async (arg, api, extraOptions, baseQuery) => {
          const { entityId, params } = arg;
          const result = await baseQuery({
            url: `/api/auth/${entityId}/agents/whatsapp/message`,
            params,
          });

          if (result.error) return { error: result.error };

          const data = result.data as PaginatedResponse<WhatsappMessageModel>;

          // Process messages to load replies if they have context.id
          const messagesWithReplies = await Promise.all(
            data.data.map(async (message) => {
              if (message.payload.context?.id) {
                try {
                  const replyResult = await baseQuery({
                    url: `/api/auth/${entityId}/agents/whatsapp/msg_id`,
                    method: "POST",
                    data: {
                      msg_id: message.payload.context.id,
                    },
                  });
                  if (replyResult.data?.data?.[0]) {
                    return {
                      ...message,
                      reply: replyResult.data.data[0] as WhatsappMessageModel,
                    };
                  }
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.error("Failed to load reply message:", error);
                }
              }
              return message;
            })
          );

          return {
            data: {
              ...data,
              data: messagesWithReplies,
            },
          };
        },
      }),
      sendWhatsappMessage: builder.mutation<
        WhatsappMessageModel,
        SendWhatsappMessageMutationParams
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/message`,
          method: "POST",
          data,
          silentError: true,
        }),
      }),
      getWhatsappProfile: builder.query<WhatsappProfile, { entityId: string }>({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/profile`,
        }),
        transformResponse: (response: { data: WhatsappProfile[] }) =>
          response.data[0],
        providesTags: [IntegrationsApiTag.whatsappProfile],
      }),
      updateWhatsappProfile: builder.mutation<
        void,
        {
          entityId: string;
          profile?: Omit<WhatsappProfile, "profile_picture_url">;
          img?: File;
        }
      >({
        query: ({ entityId, profile, img }) => {
          const formData = new FormData();
          if (profile) {
            formData.append("profile", JSON.stringify(profile));
          }
          if (img) {
            formData.append("display_pic", img);
          }

          return {
            url: `/api/auth/${entityId}/agents/whatsapp/profile`,
            method: "POST",
            data: formData,
            silentError: true,
          };
        },
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const updateWhatsappProfileResult = dispatch(
            integrationsApi.util.updateQueryData(
              "getWhatsappProfile",
              {
                entityId: args.entityId,
              },
              (whatsappProfileDraft) => ({
                ...whatsappProfileDraft,
                ...args.profile,
              })
            )
          );
          queryFulfilled.catch(() => {
            updateWhatsappProfileResult.undo();
          });
        },
        invalidatesTags: (e1, e2, args) =>
          args.img ? [IntegrationsApiTag.whatsappProfile] : [],
      }),
      setWhatsappPin: builder.mutation<
        void,
        {
          entityId: string;
          pin: string;
        }
      >({
        query: ({ entityId, pin }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/2FA`,
          method: "POST",
          data: { pin },
        }),
      }),
      updateWhatsappDisplayName: builder.mutation<
        void,
        {
          entityId: string;
          name: string;
        }
      >({
        query: ({ entityId, name }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/display_name`,
          method: "POST",
          data: { name },
          silentError: true,
        }),
      }),
      registerWhatsappAction: builder.mutation<
        void,
        {
          entityId: string;
          pin: string;
        }
      >({
        query: ({ entityId, pin }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/account/register`,
          method: "POST",
          data: { pin },
        }),
      }),
      getWhatsappQualityRating: builder.query<
        { rating: WhatsappQualityRating },
        { entityId: string }
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/quality_rating`,
        }),
      }),
      getWhatsappBusinessVerificationStatus: builder.query<
        { status: WhatsappBusinessVerificationStatus },
        { entityId: string }
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/verification_status`,
        }),
      }),
      getWhatsappSettings: builder.query<void, { entityId: string }>({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/settings`,
        }),
      }),
      updateWhatsappAgentAccount: builder.mutation<
        WhatsappAgentModel | null,
        { entityId: string; hideNumber: boolean }
      >({
        query: ({ entityId, hideNumber }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/settings`,
          method: "POST",
          data: {
            settings: { hide_num: hideNumber },
          },
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const updatewhatsappAgentAccount = dispatch(
            integrationsApi.util.updateQueryData(
              "getCRMConfig",
              {
                entityId: args.entityId,
              },
              (crmConfigDraft) => ({
                ...crmConfigDraft,
                whatsapp: crmConfigDraft.whatsapp
                  ? {
                      ...crmConfigDraft.whatsapp,
                      settings: {
                        hide_num: args.hideNumber,
                      },
                    }
                  : null,
              })
            )
          );
          queryFulfilled.catch(() => {
            updatewhatsappAgentAccount.undo();
          });
        },
        transformResponse: (data: WhatsappAgentModel | Record<string, never>) =>
          Object.keys(data).length === 0 ? null : (data as WhatsappAgentModel),
      }),
      uploadWhatsappMedia: builder.mutation<
        { id: string },
        { entityId: string; file: File }
      >({
        query: ({ entityId, file }) => {
          const formData = new FormData();
          formData.append("file", file);
          return {
            url: `/api/auth/${entityId}/agents/whatsapp/media`,
            method: "POST",
            data: formData,
            headers: { "Content-Type": "multipart/form-data" },
            silentError: true,
          };
        },
      }),
      getWhatsappMessageById: builder.query<
        { data: WhatsappMessageModel[] },
        { entityId: string; params: { msg_id: string } }
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/msg_id`,
          data: params,
          method: "POST",
        }),
      }),
      getConversationById: builder.query<
        WhatsappConversationModel,
        { entityId: string; convId: string }
      >({
        query: ({ entityId, convId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/conversation_id`,
          params: { conv_id: convId },
        }),
        transformResponse: (response: WhatsappConversationDto) =>
          transformWhatsappConversationDtoToModel(response),
      }),
      sendWhatsappMessageReaction: builder.mutation<
        void,
        {
          entityId: string;
          data: {
            to: string;
            conv_name: string;
            type: "reaction";
            payload: {
              message_id: string;
              emoji: string;
            };
          };
        }
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/message`,
          method: "POST",
          data,
        }),
      }),
      getWhatsappMessageMedia: builder.query<
        Blob,
        { entityId: string; mediaId: string }
      >({
        query: ({ entityId, mediaId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/media`,
          params: { id: mediaId },
          responseType: "blob",
        }),
        providesTags: (result, error, { mediaId }) => [
          { type: IntegrationsApiTag.whatsappMessageMedia, id: mediaId },
        ],
      }),

      // Instagram

      getInstagramConversations: builder.query<
        PaginatedResponse<InstagramConversationModel>,
        QueryParamsWithEntityId<
          PaginatedRequestParams<{
            search_term?: string;
            filter:
              | CRMConversationFilter.active
              | CRMConversationFilter.expired
              | CRMConversationMode.onAIAutoReply
              | CRMConversationMode.waitingResponse
              | string;
          }>
        >
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/agents/instagram/conversation`,
          params: {
            ...params,
            search_term: params?.search_term?.trim() || undefined,
          },
        }),
        transformResponse: (
          response: PaginatedResponse<InstagramConversationDto>
        ) => ({
          ...response,
          data: response.data.map(transformInstagramConversationDtoToModel),
        }),
      }),
      getInstagramMessages: builder.query<
        PaginatedResponse<InstagramMessageModel>,
        {
          entityId: string;
          params: PaginatedRequestParams<{
            conv_id: string;
            from: string;
          }>;
        }
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/agents/instagram/message`,
          params,
        }),
      }),
      sendInstagramMessage: builder.mutation<
        InstagramMessageModel,
        SendInstagramMessageMutationParams
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/instagram/message`,
          method: "POST",
          data,
          silentError: true,
        }),
      }),
      getInstagramMessageById: builder.query<
        { data: InstagramMessageModel[] },
        { entityId: string; params: { msg_id: string } }
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/agents/msg_id`,
          data: {
            ...params,
            module: "instagram",
          },
          method: "POST",
        }),
        providesTags: (e1, e2, { params: { msg_id } }) => [
          { type: IntegrationsApiTag.instagramMessage, id: msg_id },
        ],
      }),
      connectInstagramAccount: builder.mutation<
        InstagramAccountModel,
        {
          entityId: string;
          data: {
            code: string;
            redirect_uri: string;
          };
        }
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/instagram/connect`,
          method: "POST",
          data: { ...data },
          silentError: true,
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),
      disconnectInstagramAccount: builder.mutation<void, { entityId: string }>({
        query: ({ entityId }) => ({
          method: "POST",
          url: `/api/auth/${entityId}/agents/instagram/disconnect`,
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),
      getInstagramMessageMedia: builder.query<
        Blob,
        { entityId: string; mediaId: string }
      >({
        query: ({ entityId, mediaId }) => ({
          url: `/api/auth/${entityId}/agents/instagram/media`,
          params: { id: mediaId },
          responseType: "blob",
        }),
      }),
      uploadInstagramMedia: builder.mutation<
        { media_id: string; url: string },
        { entityId: string; file: File }
      >({
        query: ({ entityId, file }) => {
          const formData = new FormData();
          formData.append("file", file);
          return {
            url: `/api/auth/${entityId}/agents/instagram/media`,
            method: "POST",
            data: formData,
            headers: { "Content-Type": "multipart/form-data" },
            silentError: true,
          };
        },
      }),
      sendInstagramMessageReaction: builder.mutation<
        void,
        {
          entityId: string;
          data: {
            payload: {
              recipient: {
                id: string;
              };
              sender_action: "react" | "unreact";
              payload: {
                message_id: string;
                reaction?: string;
              };
            };
          };
        }
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/instagram/message`,
          method: "POST",
          data,
        }),
      }),

      // Web Chat Bot

      getWebChatBotConversations: builder.query<
        PaginatedResponse<WebChatBotConversationModel>,
        PaginatedRequestParams<{
          entityId: string;
          filter?:
            | CRMConversationMode.onAIAutoReply
            | CRMConversationMode.waitingResponse
            | string;
          search_term?: string;
        }>
      >({
        query: ({ entityId, page, page_size, search_term, filter }) => ({
          url: `/api/auth/${entityId}/agents/chatbot/conversation`,
          params: {
            page,
            page_size,
            filter,
            search_term: search_term?.trim() || undefined,
          },
        }),
        transformResponse: (
          response: PaginatedResponse<WebChatBotConversationDto>
        ) => ({
          ...response,
          data: response.data.map(transformWebChatBotConversationDtoToModel),
        }),
      }),
      getWebChatBotMessages: builder.query<
        PaginatedResponse<WebChatBotMessageModel>,
        {
          entityId: string;
          params: PaginatedRequestParams<{
            conv_id: string;
          }>;
        }
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/agents/chatbot/message`,
          params,
        }),
        transformResponse: (
          response: PaginatedResponse<WebChatBotMessageDto>
        ) => ({
          ...response,
          data: response.data.map(transformWebChatBotMessageDtoToModel),
        }),
      }),
      getChatBotToken: builder.mutation<
        { token: string },
        { entityId: string }
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/chatbot/token`,
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),
      getWebChatBotConfig: builder.query<
        WebChatBotAccountModel,
        { entityId: string }
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/chatbot/config`,
        }),
        providesTags: [IntegrationsApiTag.crmConfig],
      }),
      updateWebChatBotConfig: builder.mutation<
        WebChatBotAccountModel,
        {
          entityId: string;
          settings?: Record<string, unknown>;
          enabled?: boolean;
        }
      >({
        query: ({ entityId, settings, enabled }) => ({
          url: `/api/auth/${entityId}/agents/chatbot/config`,
          method: "PUT",
          data: {
            settings,
            enabled,
          },
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const updateWebChatBotConfigResult = dispatch(
            integrationsApi.util.updateQueryData(
              "getCRMConfig",
              {
                entityId: args.entityId,
              },
              (crmConfigDraft) => ({
                ...crmConfigDraft,
                chatbot: crmConfigDraft.chatbot
                  ? {
                      ...crmConfigDraft.chatbot,
                      settings:
                        args.settings ?? crmConfigDraft.chatbot.settings,
                      enabled: args.enabled ?? crmConfigDraft.chatbot.enabled,
                    }
                  : null,
              })
            )
          );
          const updateGetWebChatBotConfigResult = dispatch(
            integrationsApi.util.updateQueryData(
              "getWebChatBotConfig",
              {
                entityId: args.entityId,
              },
              (webChatBotConfigDraft) => ({
                ...webChatBotConfigDraft,
                settings: args.settings ?? webChatBotConfigDraft.settings,
                enabled: args.enabled ?? webChatBotConfigDraft.enabled,
              })
            )
          );
          queryFulfilled.catch(() => {
            updateWebChatBotConfigResult.undo();
            updateGetWebChatBotConfigResult.undo();
          });
        },
      }),
      uploadChatBotFiles: builder.mutation<
        { status: string },
        {
          entityId: string;
          files: {
            file: File;
            type:
              | "launcher_icon"
              | "avatar_icon"
              | "chat_icon"
              | "welcome_msg_media";
          }[];
        }
      >({
        query: ({ entityId, files }) => {
          const formData = new FormData();

          files.forEach(({ file, type }) => {
            formData.append(`files[${type}]`, file);
          });

          return {
            url: `/api/auth/${entityId}/agents/chatbot/upload_files`,
            method: "POST",
            headers: { "Content-Type": "multipart/form-data" },
            data: formData,
          };
        },
      }),
      getWebChatBotCustomerInsights: builder.query<
        AICustomerInsights,
        { entityId: string; conversationId: string }
      >({
        query: ({ entityId, conversationId }) => ({
          url: `/api/auth/${entityId}/crm_poc/${conversationId}/insights`,
        }),
      }),
      disconnectWebChatBot: builder.mutation<void, { entityId: string }>({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/chatbot/disconnect`,
          method: "POST",
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),

      // Integration

      getWorkatoEmbeddedUrl: builder.query<
        { url: string },
        { entityId: string }
      >({
        query: ({ entityId }) => ({
          url: `/agents/workato/embedded/${entityId}`,
        }),
      }),

      getTwilioConversations: builder.query<
        PaginatedResponse<TwilioConversationModel>,
        PaginatedRequestParams<{
          entityId: string;
          search_term: string;
          filter?:
            | CRMConversationMode.onAIAutoReply
            | CRMConversationMode.waitingResponse
            | string;
        }>
      >({
        query: ({ entityId, page, page_size, filter, search_term }) => ({
          url: `/api/auth/${entityId}/agents/twilio/conversation`,
          params: {
            page,
            page_size,
            filter,
            search_term: search_term?.trim() || undefined,
          },
        }),
        transformResponse: (response: PaginatedResponse<any>) => ({
          ...response,
          data: response.data.map(transformTwilioMessageDtoToModel),
        }),
      }),

      getTwilioMessages: builder.query<
        PaginatedResponse<TwilioMessageModel>,
        {
          entityId: string;
          params: PaginatedRequestParams<{
            conv_id: string;
          }>;
        }
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/agents/twilio/message`,
          params,
        }),
        transformResponse: (response: PaginatedResponse<any>) => ({
          ...response,
          data: response.data.map(transformTwilioMessageDtoToModel),
        }),
      }),

      sendTwilioMessage: builder.mutation<
        TwilioMessageModel,
        SendTwilioMessageMutationParams
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/twilio/message`,
          method: "POST",
          data,
        }),
        transformResponse: (response: any) =>
          transformTwilioMessageDtoToModel(response),
      }),
      getWorkflows: builder.query<
        PaginatedResponse<Workflow>,
        PaginatedRequestParams<{ entityId: string }>
      >({
        query: ({ entityId, ...params }) => ({
          url: `/api/auth/${entityId}/workflows`,
          params: {
            ...params,
          },
        }),
      }),
      getWorkflow: builder.query<
        Workflow,
        { entityId: string; workflowId: string }
      >({
        query: ({ entityId, workflowId }) => ({
          url: `/api/auth/${entityId}/workflows/${workflowId}`,
        }),
      }),
      createWorkflow: builder.mutation<
        Workflow,
        { entityId: string; data: WorkflowParams }
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/workflows`,
          method: "POST",
          data,
          silentError: true,
        }),
      }),
      updateWorkflow: builder.mutation<
        Workflow,
        { entityId: string; workflowId: string; data: Partial<WorkflowParams> }
      >({
        query: ({ entityId, workflowId, data }) => ({
          url: `/api/auth/${entityId}/workflows/${workflowId}`,
          method: "PUT",
          data,
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const updateWorkflow = dispatch(
            integrationsApi.util.updateQueryData(
              "getWorkflow",
              {
                entityId: args.entityId,
                workflowId: args.workflowId,
              },
              (updatedWorkflowDraft) => ({
                ...updatedWorkflowDraft,
                ...args.data,
                updated_at: new Date().toISOString(),
              })
            )
          );
          queryFulfilled.catch(() => {
            updateWorkflow.undo();
          });
        },
      }),
      deleteWorkflow: builder.mutation<
        void,
        { entityId: string; workflowId: string }
      >({
        query: ({ entityId, workflowId }) => ({
          url: `/api/auth/${entityId}/workflows/${workflowId}`,
          method: "DELETE",
        }),
      }),
      getWorkflowLogs: builder.query<
        PaginatedResponse<WorkflowLog>,
        PaginatedRequestParams<{ entityId: string; workflowId: string }>
      >({
        query: ({ entityId, workflowId, ...params }) => ({
          url: `/api/auth/${entityId}/workflows/${workflowId}/logs`,
          params: {
            ...params,
          },
        }),
        providesTags: (_result, _error, { workflowId, page }) => [
          {
            type: IntegrationsApiTag.workflowLogs,
            id: `${workflowId}-${page}`,
          },
          { type: IntegrationsApiTag.workflowLogs, id: workflowId },
        ],
      }),
      getAIAgentInsights: builder.query<
        AIAgentInsights,
        {
          entityId: string;
          channel: CRMChannel;
          start_time: string;
          end_time: string;
        }
      >({
        query: ({ entityId, ...params }) => ({
          url: `/api/auth/${entityId}/agents/stats`,
          params: {
            ...params,
          },
        }),
      }),
      getAIAgentAnalytics: builder.query<
        AIAgentAnalyticsItemModel[],
        { entityId: string; fromISO: string; toISO: string }
      >({
        query: ({ entityId, fromISO, toISO }) => ({
          url: `/api/auth/${entityId}/cx/agent/analytics`,
          params: {
            from: fromISO,
            to: toISO,
          },
        }),
        transformResponse: (response: { data: AIAgentAnalyticsItemDto[] }) =>
          transformAIAgentAnalyticsItemsDtoToModels(response.data),
      }),
      getCRMUnreadCounts: builder.query<CRMUnreadCounts, { entityId: string }>({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/unread`,
        }),
      }),
      getCRMConfig: builder.query<
        {
          chatbot: WebChatBotAccountModel | null;
          instagram: InstagramAccountModel | null;
          twilio: TwilioAccountModel | null;
          whatsapp: WhatsappAgentModel | null;
        },
        { entityId: string }
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/config`,
        }),
        providesTags: [IntegrationsApiTag.crmConfig],
      }),
      markConversationAsRead: builder.mutation<
        { result: string },
        { entityId: string; data: MarkConversationAsReadParams }
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/read`,
          method: "POST",
          data,
        }),
      }),

      // Shopify

      connectShopifyAccount: builder.mutation<
        Record<string, unknown>,
        {
          entityId: string;
          data: {
            type: "shopify";
            name: string;
            data: {
              session_token: string;
              hmac: string;
              store: string;
            };
          };
        }
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/connections`,
          method: "POST",
          data,
          silentError: true,
        }),
        invalidatesTags: [IntegrationsApiTag.shopifyAccount],
      }),
      getShopifyAccount: builder.query<
        Record<string, unknown>,
        { entityId: string }
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/shopify/config`,
        }),
        providesTags: [IntegrationsApiTag.shopifyAccount],
      }),
      disconnectShopifyAccount: builder.mutation<void, { entityId: string }>({
        query: ({ entityId }) => ({
          method: "POST",
          url: `/api/auth/${entityId}/agents/shopify/disconnect`,
        }),
        invalidatesTags: [IntegrationsApiTag.shopifyAccount],
      }),

      // Connections (Calendly, Squareup)

      getConnections: builder.query<
        {
          data: ConnectionDto[];
          page_number: number;
          page_size: number;
          total_entries: number;
          total_pages: number;
        },
        { entityId: string; type?: ConnectionType }
      >({
        query: ({ entityId, type }) => ({
          url: `/api/auth/${entityId}/connections`,
          params: type ? { type } : undefined,
        }),
        providesTags: (_result, _error, { type }) => {
          if (!type) {
            return [
              IntegrationsApiTag.calendlyConnection,
              IntegrationsApiTag.squareupConnection,
              IntegrationsApiTag.shopifyAccount,
            ];
          }
          if (type === "calendly")
            return [IntegrationsApiTag.calendlyConnection];
          if (type === "squareup")
            return [IntegrationsApiTag.squareupConnection];
          return [IntegrationsApiTag.shopifyAccount];
        },
      }),
      getCalendlyEventTypes: builder.query<
        { data: CalendlyEventType[] },
        { entityId: string; connectionId: string }
      >({
        query: ({ entityId, connectionId }) => ({
          url: `/api/auth/${entityId}/connections/${connectionId}/event_types`,
        }),
      }),
      getSquareupLocations: builder.query<
        { data: SquareupLocation[] },
        { entityId: string; connectionId: string }
      >({
        query: ({ entityId, connectionId }) => ({
          url: `/api/auth/${entityId}/connections/${connectionId}/locations`,
        }),
      }),
      setCalendlyEventType: builder.mutation<
        { result: string },
        {
          entityId: string;
          connectionId: string;
          eventType: CalendlyEventType;
        }
      >({
        query: ({ entityId, connectionId, eventType }) => ({
          method: "PUT",
          url: `/api/auth/${entityId}/connections/${connectionId}/set_event_type`,
          data: { data: { event_type: eventType } },
        }),
        invalidatesTags: [IntegrationsApiTag.calendlyConnection],
      }),
      setSquareupLocation: builder.mutation<
        { result: string },
        {
          entityId: string;
          connectionId: string;
          location: SquareupLocation;
        }
      >({
        query: ({ entityId, connectionId, location }) => ({
          method: "PUT",
          url: `/api/auth/${entityId}/connections/${connectionId}/set_location`,
          data: { data: { location } },
        }),
        invalidatesTags: [IntegrationsApiTag.squareupConnection],
      }),
      deleteConnection: builder.mutation<
        void,
        { entityId: string; connectionId: string }
      >({
        query: ({ entityId, connectionId }) => ({
          method: "DELETE",
          url: `/api/auth/${entityId}/connections/${connectionId}`,
        }),
        invalidatesTags: [
          IntegrationsApiTag.calendlyConnection,
          IntegrationsApiTag.squareupConnection,
        ],
      }),

      // Agent Config

      getAiAgentConfig: builder.query<AgentConfigDto, { entityId: string }>({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/${entityId}/configs`,
          silentError: true,
        }),
        providesTags: [IntegrationsApiTag.agentConfig],
      }),
      createAgentConfig: builder.mutation<
        AgentConfigDto,
        { entityId: string; config: AgentConfigData }
      >({
        query: ({ entityId, config }) => ({
          method: "POST",
          url: `/api/auth/${entityId}/agents/${entityId}/configs`,
          data: { config },
        }),
        invalidatesTags: [IntegrationsApiTag.agentConfig],
      }),
      updateAgentConfig: builder.mutation<
        AgentConfigDto,
        { entityId: string; config: Partial<AgentConfigData> }
      >({
        query: ({ entityId, config }) => ({
          method: "PUT",
          url: `/api/auth/${entityId}/agents/${entityId}/configs`,
          data: { config },
        }),
        invalidatesTags: [IntegrationsApiTag.agentConfig],
      }),
    }),
  });

  return integrationsApi;
};

export type IntegrationsApi = ReturnType<typeof addIntegrationsApi>;
